import { Controller } from '@hotwired/stimulus'
import { Modal } from 'bootstrap'
import 'semantic-ui-css/components/label.min.css'
import 'semantic-ui-checkbox/checkbox.min.css'
import 'semantic-ui-checkbox/checkbox.min.js'
import fetchWithHandlingLoginRedirect from '../../modules/utils/fetchWithHandlingLoginRedirect'
import {
  trans, WEBHOOKS_ACTION_NEW, EDIT,
  WEBHOOKS_FILTERS_EVENT_COUNTER,
  WEBHOOKS_FILTERS_SEVERITY_WARNING, WEBHOOKS_FILTERS_SEVERITY_INFO,
  WEBHOOKS_FILTERS_NOTICE_NEVER_DELIVERED_HEADING, WEBHOOKS_FILTERS_NOTICE_NEVER_DELIVERED_BODY,
  WEBHOOKS_FILTERS_NOTICE_UPDATE_FIRST_HEADING, WEBHOOKS_FILTERS_NOTICE_UPDATE_FIRST_BODY,
  WEBHOOKS_FILTERS_NOTICE_CREATED_UNSELECTED_BODY
} from '../../translator'
import {
  EventFilters, FilterOption, IssueFilterOptions, FilterSelection,
  fieldsFor as getFieldsFor, isFilterable as eventIsFilterable,
  optionByIdForEvent as findOptionById,
  isDescendantOfSelectedGroup as isSelectedGroupAncestor
} from './webhook-filter-options'
import { recordWord as formatRecordWord, summaryFor } from './webhook-filter-formatters'
import { buildPanel as renderFilterPanel } from './webhook-filter-panel'

declare const $: any

const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' }

interface WebhookSubscriptionResult {
  id: string
  url: string
  event_types: string[]
  event_filters?: EventFilters
  is_active: boolean
  created_at: string
  secret?: string
}

type IdParamEvent = Event & { params: { id: string } }

export default class extends Controller {
  static targets = [
    'modal', 'form', 'urlField', 'urlError', 'eventTypesField', 'eventTypesError', 'title',
    'secretModal', 'secretText', 'copySecretButton', 'deleteModal', 'rotateModal',
    'eventRow', 'eventChevron', 'eventSummary', 'eventMenu', 'eventPanel',
    'lifecycleNotice', 'eventCounter'
  ]

  static values = {
    createUrl: String,
    issueFilterOptions: Object
  }

  declare createUrlValue: string
  declare issueFilterOptionsValue: IssueFilterOptions
  declare readonly modalTarget: HTMLElement
  declare readonly formTarget: HTMLFormElement
  declare readonly urlFieldTarget: HTMLInputElement
  declare readonly urlErrorTarget: HTMLElement
  declare readonly eventTypesFieldTarget: HTMLElement
  declare readonly eventTypesErrorTarget: HTMLElement
  declare readonly titleTarget: HTMLElement
  declare readonly secretModalTarget: HTMLElement
  declare readonly secretTextTarget: HTMLElement
  declare readonly copySecretButtonTarget: HTMLElement
  declare readonly deleteModalTarget: HTMLElement
  declare readonly rotateModalTarget: HTMLElement
  declare readonly eventRowTargets: HTMLElement[]
  declare readonly eventChevronTargets: HTMLElement[]
  declare readonly eventSummaryTargets: HTMLElement[]
  declare readonly eventMenuTargets: HTMLElement[]
  declare readonly lifecycleNoticeTargets: HTMLElement[]
  declare readonly eventCounterTarget: HTMLElement

  private modal!: Modal
  private secretModal!: Modal
  private deleteModal!: Modal
  private rotateModal!: Modal

  editId: string | null = null
  pendingId: string | null = null

  private filters: EventFilters = {}
  private suppressSelectionSync = false
  private expanded = new Set<string>()
  private openDropdown: string | null = null
  private builtPanels = new Set<string>()

  connect (): void {
    this.modal = new Modal(this.modalTarget)
    this.secretModal = new Modal(this.secretModalTarget)
    this.deleteModal = new Modal(this.deleteModalTarget)
    this.rotateModal = new Modal(this.rotateModalTarget)
    $(this.copySecretButtonTarget).tooltip()
    // Semantic UI owns the checkbox, so its own callback is the reliable signal. The template also
    // declares a change action, which covers the case where Semantic UI has not initialised.
    const controller = this
    $(this.eventTypesFieldTarget).find('.ui.checkbox').checkbox({
      onChange (this: HTMLInputElement) {
        if (!controller.suppressSelectionSync) {
          controller.syncEventSelection(this)
        }
      }
    })
    this.render()
  }

  openCreateModal (): void {
    this.editId = null
    this.titleTarget.textContent = trans(WEBHOOKS_ACTION_NEW, {}, 'integrations')
    this.resetForm()
    this.clearErrors()
    this.render()
    this.modal.show()
  }

  openEditModal (event: Event & { params: { id: string, url: string, eventTypes?: string[], eventFilters?: EventFilters } }): void {
    this.editId = event.params.id
    this.titleTarget.textContent = trans(EDIT, {}, 'actions')
    this.urlFieldTarget.value = event.params.url
    const eventTypes = event.params.eventTypes ?? []
    this.withSelectionSyncSuppressed(() => {
      this.eventTypesFieldTarget.querySelectorAll('.ui.checkbox').forEach((el) => {
        const checkbox = el.querySelector('input[type="checkbox"]') as HTMLInputElement | null
        if (checkbox) {
          $(el).checkbox(eventTypes.includes(checkbox.value) ? 'check' : 'uncheck')
        }
      })
    })

    // Saved filters are hydrated as-is, including values that are no longer selectable, so nothing is
    // silently dropped from a subscription just by opening it.
    this.filters = this.hydrateFilters(event.params.eventFilters)
    this.expanded = new Set(Object.keys(this.filters).filter((id) => eventTypes.includes(id)))
    this.discardBuiltPanels()

    this.clearErrors()
    this.render()
    this.modal.show()
  }

  private hydrateFilters (saved?: EventFilters): EventFilters {
    if (saved === undefined || saved === null || Array.isArray(saved)) {
      return {}
    }

    const hydrated: EventFilters = {}

    Object.keys(saved).forEach((eventType) => {
      const fields = saved[eventType]
      if (typeof fields !== 'object' || fields === null) {
        return
      }
      Object.keys(fields).forEach((field) => {
        const selections = fields[field]
        if (!Array.isArray(selections)) {
          return
        }
        hydrated[eventType] = hydrated[eventType] ?? {}
        hydrated[eventType][field] = selections
          .filter((s) => typeof s?.id === 'string' && typeof s?.kind === 'string')
          .map((s) => ({ kind: s.kind, id: s.id }))
      })
    })

    return hydrated
  }

  private withSelectionSyncSuppressed (work: () => void): void {
    this.suppressSelectionSync = true
    try {
      work()
    } finally {
      this.suppressSelectionSync = false
    }
  }

  private resetForm (): void {
    this.urlFieldTarget.value = ''
    this.withSelectionSyncSuppressed(() => {
      $(this.eventTypesFieldTarget).find('.ui.checkbox').checkbox('uncheck')
    })
    this.filters = {}
    this.expanded = new Set<string>()
    this.discardBuiltPanels()
  }

  // -------------------------------------------------------------------------
  // Issue event filters
  // -------------------------------------------------------------------------

  private selectedEventTypes (): string[] {
    return this.eventRowTargets
      .filter((row) => row.querySelector<HTMLInputElement>('.ui.checkbox input[type="checkbox"]')?.checked === true)
      .map((row) => row.dataset.eventType ?? '')
      .filter((eventType) => eventType !== '')
  }

  private isFilterable (eventType: string): boolean {
    return eventIsFilterable(eventType)
  }

  private fieldsFor (eventType: string): string[] {
    return getFieldsFor(eventType)
  }

  private recordWord (recordType: string, form: 'singular' | 'plural' | 'article'): string {
    return formatRecordWord(recordType, form)
  }

  private selections (eventType: string, field: string): FilterSelection[] {
    return this.filters[eventType]?.[field] ?? []
  }

  private renderDerived (): void {
    const selected = this.selectedEventTypes()

    this.eventRowTargets.forEach((row) => {
      const eventType = row.dataset.eventType ?? ''
      const filterable = selected.includes(eventType) && this.isFilterable(eventType)
      const summary = row.querySelector<HTMLElement>('[data-save-webhook-modal-target~="eventSummary"]')

      if (summary !== null && summary !== undefined) {
        summary.textContent = filterable && !this.expanded.has(eventType) ? this.summaryFor(eventType) : ''
      }
    })

    this.renderCounter(selected)
    this.renderLifecycleNotices(selected)
  }

  private populatedFieldCount (eventType: string): number {
    return this.fieldsFor(eventType).filter((field) => this.selections(eventType, field).length > 0).length
  }

  private summaryFor (eventType: string): string {
    return summaryFor(eventType, this.filters, this.issueFilterOptionsValue)
  }

  toggleEventFilters (event: Event): void {
    event.preventDefault()
    const eventType = this.eventTypeOf(event.currentTarget as HTMLElement)
    if (eventType === null || !this.selectedEventTypes().includes(eventType)) {
      return
    }
    if (this.expanded.has(eventType)) {
      this.expanded.delete(eventType)
    } else {
      this.expanded.add(eventType)
    }
    this.render()
  }

  clearEventFilters (event: Event): void {
    event.preventDefault()
    const eventType = this.eventTypeOf(event.currentTarget as HTMLElement)
    if (eventType === null) {
      return
    }
    delete this.filters[eventType]

    const panel = this.panelFor(eventType)
    if (panel !== null) {
      this.teardownPanel(panel)
      this.builtPanels.delete(eventType)
    }

    this.render()
  }

  removeEvent (event: Event): void {
    event.preventDefault()
    const eventType = this.eventTypeOf(event.currentTarget as HTMLElement)
    if (eventType === null) {
      return
    }
    const row = this.rowFor(eventType)
    const checkbox = row?.querySelector('.ui.checkbox')
    this.withSelectionSyncSuppressed(() => {
      if (checkbox !== null && checkbox !== undefined) {
        $(checkbox).checkbox('uncheck')
      }
    })
    this.syncEventSelection(null)
  }

  onEventSelectionChanged (event: Event): void {
    if (event.target instanceof HTMLInputElement && event.target.type === 'checkbox') {
      this.syncEventSelection(event.target)
    }
  }

  /**
   * Deselecting an event drops its filters: leaving them behind would resubmit filters for an event
   * that is no longer selected, which the server rejects.
   */
  private syncEventSelection (changed: HTMLInputElement | null): void {
    const selected = this.selectedEventTypes()

    Object.keys(this.filters).forEach((eventType) => {
      if (!selected.includes(eventType)) {
        delete this.filters[eventType]
        this.expanded.delete(eventType)
      }
    })

    // A deselected event's panel is stale: its next expansion must reflect the cleared filters.
    this.eventRowTargets
      .map((row) => row.dataset.eventType ?? '')
      .filter((eventType) => !selected.includes(eventType) && this.builtPanels.has(eventType))
      .forEach((eventType) => {
        const panel = this.panelFor(eventType)
        if (panel !== null) {
          this.teardownPanel(panel)
        }
        this.builtPanels.delete(eventType)
      })

    // Ticking an event opens it straight away, so its filters are there when you want them.
    const ticked = changed?.checked === true ? this.eventTypeOf(changed) : null
    if (ticked !== null && this.isFilterable(ticked)) {
      this.expanded.add(ticked)
    }

    this.clearEventTypesErrorIfValid()
    this.render()
  }

  /** Detaches Semantic UI before the markup goes, so it leaves no handlers or menus behind. */
  private teardownPanel (panel: HTMLElement): void {
    panel.querySelectorAll<HTMLElement>('.webhook-filter-dropdown').forEach((element) => {
      $(element).dropdown('destroy')
    })
    panel.replaceChildren()
  }

  private discardBuiltPanels (): void {
    this.eventRowTargets.forEach((row) => {
      const panel = this.panelFor(row.dataset.eventType ?? '')
      if (panel !== null && panel.childElementCount > 0) {
        this.teardownPanel(panel)
      }
    })
    this.builtPanels.clear()
  }

  private eventTypeOf (element: HTMLElement): string | null {
    return element.closest<HTMLElement>('[data-event-type]')?.dataset.eventType ?? null
  }

  private rowFor (eventType: string): HTMLElement | undefined {
    return this.eventRowTargets.find((row) => row.dataset.eventType === eventType)
  }

  private panelFor (eventType: string): HTMLElement | null {
    return this.rowFor(eventType)?.querySelector<HTMLElement>('[data-save-webhook-modal-target~="eventPanel"]') ?? null
  }

  private render (): void {
    const selected = this.selectedEventTypes()

    this.eventRowTargets.forEach((row) => {
      const eventType = row.dataset.eventType ?? ''
      const isSelected = selected.includes(eventType)
      const filterable = isSelected && this.isFilterable(eventType)
      const isExpanded = filterable && this.expanded.has(eventType)

      const chevron = row.querySelector<HTMLElement>('[data-save-webhook-modal-target~="eventChevron"]')
      const menu = row.querySelector<HTMLElement>('[data-save-webhook-modal-target~="eventMenu"]')
      const summary = row.querySelector<HTMLElement>('[data-save-webhook-modal-target~="eventSummary"]')
      const panel = row.querySelector<HTMLElement>('[data-save-webhook-modal-target~="eventPanel"]')

      chevron?.classList.toggle('d-none', !filterable)
      chevron?.setAttribute('aria-expanded', isExpanded ? 'true' : 'false')
      chevron?.querySelector('i')?.classList.toggle('fa-angle-down', isExpanded)
      chevron?.querySelector('i')?.classList.toggle('fa-angle-right', !isExpanded)
      menu?.classList.toggle('d-none', !filterable)

      if (summary !== null && summary !== undefined) {
        summary.textContent = filterable && !isExpanded ? this.summaryFor(eventType) : ''
      }

      if (panel !== null && panel !== undefined) {
        panel.classList.toggle('d-none', !isExpanded)

        if (isExpanded && !this.builtPanels.has(eventType)) {
          panel.replaceChildren(this.buildPanel(eventType))
          this.builtPanels.add(eventType)
          this.initialiseFieldDropdowns(eventType, panel)
        } else if (!isExpanded && this.builtPanels.has(eventType)) {
          this.teardownPanel(panel)
          this.builtPanels.delete(eventType)
        }
      }
    })

    this.renderCounter(selected)
    this.renderLifecycleNotices(selected)
  }

  private renderCounter (selected: string[]): void {
    const total = this.eventRowTargets.length
    const filtered = selected.filter((eventType) => this.populatedFieldCount(eventType) > 0).length

    this.eventCounterTarget.textContent = trans(
      WEBHOOKS_FILTERS_EVENT_COUNTER,
      { '%selected%': String(selected.length), '%total%': String(total), '%filtered%': String(filtered) },
      'integrations'
    )
  }

  private buildPanel (eventType: string): DocumentFragment {
    return renderFilterPanel(eventType, { filters: this.filters, options: this.issueFilterOptionsValue })
  }

  private updateGroupDescendantVisibility (eventType: string, field: string, dropdown: HTMLElement): void {
    const selectedIds = this.selections(eventType, field).map((selection) => selection.id)

    dropdown.querySelectorAll<HTMLElement>('.menu .item').forEach((item) => {
      const option = findOptionById(this.issueFilterOptionsValue, field, item.dataset.value ?? '', eventType)
      const hidden = option !== undefined && isSelectedGroupAncestor(this.issueFilterOptionsValue, field, option, selectedIds) && !selectedIds.includes(option.id)
      item.classList.toggle('d-none', hidden)
    })
  }

  private refreshFieldDropdownOptions (eventType: string, field: string, dropdown: HTMLElement): void {
    const retainedIds = this.selections(eventType, field).map((selection) => selection.id)
    this.withSelectionSyncSuppressed(() => $(dropdown).dropdown('set exactly', retainedIds))
    this.updateGroupDescendantVisibility(eventType, field, dropdown)
  }

  /**
   * Semantic UI has to be attached after the markup is in the document, and the panel is only built
   * on expand, so selecting a value never tears the open dropdown down underneath the cursor.
   */
  private initialiseFieldDropdowns (eventType: string, panel: HTMLElement): void {
    panel.querySelectorAll<HTMLElement>('.webhook-filter-dropdown').forEach((element) => {
      const field = element.dataset.field ?? ''

      $(element).dropdown({
        fullTextSearch: true,
        forceSelection: false,
        clearable: true,
        onChange: (value: string) => {
          if (this.suppressSelectionSync) {
            return
          }
          this.onFieldDropdownChanged(eventType, field, value)
        }
      })

      const selectedIds = this.selections(eventType, field)
        .map((selection) => selection.id)
        .filter((id) => findOptionById(this.issueFilterOptionsValue, field, id, eventType) !== undefined)

      this.withSelectionSyncSuppressed(() => {
        $(element).dropdown('set exactly', selectedIds)
      })
      this.updateGroupDescendantVisibility(eventType, field, element)
    })
  }

  /**
   * Semantic UI reports a comma-separated list of ids. The kind is recovered from the options rather
   * than the DOM, so a group selection stays a group.
   */
  private onFieldDropdownChanged (eventType: string, field: string, value: string): void {
    const ids = (value ?? '').split(',').map((id) => id.trim()).filter((id) => id !== '')

    this.filters[eventType] = this.filters[eventType] ?? {}
    const selections = ids.map((id) => ({
      kind: findOptionById(this.issueFilterOptionsValue, field, id, eventType)?.kind ?? 'leaf',
      id
    }))
    const selectedGroupIds = selections.filter((selection) => selection.kind === 'group').map((selection) => selection.id)
    this.filters[eventType][field] = selections.filter((selection) => {
      const option = findOptionById(this.issueFilterOptionsValue, field, selection.id, eventType)
      return !isSelectedGroupAncestor(this.issueFilterOptionsValue, field, option, selectedGroupIds) || selectedGroupIds.includes(selection.id)
    })

    const dropdown = this.panelFor(eventType)?.querySelector<HTMLElement>(`.webhook-filter-dropdown[data-field="${field}"]`)
    if (dropdown !== null && dropdown !== undefined) {
      this.refreshFieldDropdownOptions(eventType, field, dropdown)
    }

    // Only the derived parts change: rebuilding the panel here would close the dropdown mid-use.
    this.renderDerived()
  }

  /**
   * One notice per record type, beneath its pair of rows.
   *
   * Filtering each event independently means a record can start matching only after it is created, so
   * the creation event is simply never sent — nothing is generated retrospectively. These notices say
   * which shape of gap the current configuration produces. Severity is carried by an icon and a text
   * label as well as colour, so it survives a colour-blind or greyscale reading.
   */
  private renderLifecycleNotices (selected: string[]): void {
    this.lifecycleNoticeTargets.forEach((container) => {
      const recordType = container.dataset.recordType ?? ''
      container.replaceChildren()

      const createdEvent = `${recordType}.created`
      const updatedEvent = `${recordType}.updated`
      const createdSelected = selected.includes(createdEvent)
      const updatedSelected = selected.includes(updatedEvent)
      const createdFiltered = createdSelected && this.populatedFieldCount(createdEvent) > 0
      const updatedFiltered = updatedSelected && this.populatedFieldCount(updatedEvent) > 0

      const params = {
        '%record%': this.recordWord(recordType, 'singular'),
        '%plural%': this.recordWord(recordType, 'plural'),
        '%article%': this.recordWord(recordType, 'article'),
        '%created_event%': createdEvent,
        '%updated_event%': updatedEvent
      }

      let notice: { severe: boolean, heading: string | null, body: string } | null = null

      if (createdSelected && !createdFiltered) {
        // An unfiltered creation event always arrives first, so there is no gap to warn about.
        notice = null
      } else if (createdFiltered && !updatedSelected) {
        notice = {
          severe: true,
          heading: trans(WEBHOOKS_FILTERS_NOTICE_NEVER_DELIVERED_HEADING, params, 'integrations'),
          body: trans(WEBHOOKS_FILTERS_NOTICE_NEVER_DELIVERED_BODY, params, 'integrations')
        }
      } else if (updatedFiltered && (!createdSelected || createdFiltered)) {
        notice = {
          severe: false,
          heading: trans(WEBHOOKS_FILTERS_NOTICE_UPDATE_FIRST_HEADING, params, 'integrations'),
          body: trans(WEBHOOKS_FILTERS_NOTICE_UPDATE_FIRST_BODY, params, 'integrations')
        }
      } else if (!createdSelected && updatedSelected && !updatedFiltered) {
        notice = {
          severe: false,
          heading: null,
          body: trans(WEBHOOKS_FILTERS_NOTICE_CREATED_UNSELECTED_BODY, params, 'integrations')
        }
      }

      if (notice === null) {
        return
      }

      container.appendChild(this.buildLifecycleNotice(recordType, notice))
    })
  }

  private buildLifecycleNotice (
    recordType: string,
    notice: { severe: boolean, heading: string | null, body: string }
  ): HTMLElement {
    const alert = document.createElement('div')
    alert.className = `alert ${notice.severe ? 'alert-danger' : 'alert-secondary'} mb-0 webhook-lifecycle-alert`
    alert.setAttribute('role', notice.severe ? 'alert' : 'status')

    const severity = document.createElement('div')
    severity.className = 'd-flex align-items-center mb-1'

    const icon = document.createElement('i')
    icon.className = `fas ${notice.severe ? 'fa-exclamation-triangle' : 'fa-info-circle'} mr-2`
    icon.setAttribute('aria-hidden', 'true')
    severity.appendChild(icon)

    const severityLabel = document.createElement('span')
    severityLabel.className = 'text-uppercase small font-weight-bold webhook-lifecycle-severity'
    severityLabel.textContent = notice.severe
      ? trans(WEBHOOKS_FILTERS_SEVERITY_WARNING, {}, 'integrations')
      : trans(WEBHOOKS_FILTERS_SEVERITY_INFO, {}, 'integrations')
    severity.appendChild(severityLabel)
    alert.appendChild(severity)

    if (notice.heading !== null) {
      const heading = document.createElement('p')
      heading.className = 'mb-1 font-weight-bold webhook-lifecycle-heading'
      heading.textContent = notice.heading
      alert.appendChild(heading)
    }

    const body = document.createElement('p')
    body.className = 'mb-1 small webhook-lifecycle-body'
    body.textContent = notice.body
    alert.appendChild(body)

    return alert
  }

  /** Filters for selected, filterable events only, dropping fields with nothing chosen. */
  private buildEventFiltersPayload (selectedEventTypes: string[]): EventFilters {
    const payload: EventFilters = {}

    selectedEventTypes.filter((eventType) => this.isFilterable(eventType)).forEach((eventType) => {
      const fields: Record<string, FilterSelection[]> = {}

      this.fieldsFor(eventType).forEach((field) => {
        const selections = this.selections(eventType, field)
        if (selections.length > 0) {
          fields[field] = selections
        }
      })

      if (Object.keys(fields).length > 0) {
        payload[eventType] = fields
      }
    })

    return payload
  }

  clearUrlErrorIfValid (): void {
    if (this.urlFieldTarget.value.startsWith('https://')) {
      this.clearFieldError('url')
    }
  }

  clearEventTypesErrorIfValid (): void {
    if (this.selectedEventTypes().length > 0) {
      this.clearFieldError('event_types')
    }
  }

  private fieldErrorElements (field: string): { errorTarget: HTMLElement, fieldWrapper: Element } {
    if (field === 'event_types' || field === 'event_filters') {
      return { errorTarget: this.eventTypesErrorTarget, fieldWrapper: this.eventTypesFieldTarget }
    }
    return { errorTarget: this.urlErrorTarget, fieldWrapper: this.urlFieldTarget.closest('.field') as Element }
  }

  private clearErrors (): void {
    this.clearFieldError('url')
    this.clearFieldError('event_types')
  }

  private clearFieldError (field: string): void {
    const { errorTarget, fieldWrapper } = this.fieldErrorElements(field)
    errorTarget.textContent = ''
    errorTarget.style.display = 'none'
    fieldWrapper.classList.remove('error')
  }

  private showFieldError (field: string, message: string): void {
    const { errorTarget, fieldWrapper } = this.fieldErrorElements(field)
    errorTarget.textContent = message
    errorTarget.style.display = 'block'
    fieldWrapper.classList.add('error')
  }

  private async parseError (response: any): Promise<{ message: string, field?: string }> {
    try {
      const body = await response.json()
      return { message: body.detail || body.message || body.error || '', field: body.field }
    } catch (e) {
      return { message: '', field: undefined }
    }
  }

  async submit (event: Event): Promise<void> {
    event.preventDefault()

    const url = this.urlFieldTarget.value
    const eventTypes = this.selectedEventTypes()

    const isEdit = this.editId !== null
    const endpoint = isEdit ? `${this.createUrlValue}/${this.editId}` : this.createUrlValue

    try {
      const response = await fetchWithHandlingLoginRedirect(endpoint, {
        method: isEdit ? 'PATCH' : 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({ url, event_types: eventTypes, event_filters: this.buildEventFiltersPayload(eventTypes) })
      })

      const body = await response.json() as { result: WebhookSubscriptionResult }

      this.clearErrors()
      this.modal.hide()

      this.dispatch('submitted', { detail: { action: isEdit ? 'updated' : 'created', subscription: body.result } })

      if (!isEdit) {
        this.showSecret(body.result.secret as string)
      }
    } catch (e) {
      const { message, field } = await this.parseError(e)
      this.showFieldError(field || 'url', message)
    }
  }

  private showSecret (secret: string): void {
    this.secretTextTarget.textContent = secret
    this.secretModal.show()
  }

  closeSecretModal (): void {
    this.secretTextTarget.textContent = ''
    this.secretModal.hide()
  }

  copySecret (): void {
    const text = this.secretTextTarget.textContent ?? ''
    const btn = this.copySecretButtonTarget

    const showCopied = (): void => {
      $(btn).tooltip('show')
      setTimeout(() => $(btn).tooltip('hide'), 1500)
    }

    const copyUsingSelection = (): void => {
      const range = document.createRange()
      range.selectNodeContents(this.secretTextTarget)
      const selection = window.getSelection()
      selection?.removeAllRanges()
      selection?.addRange(range)
      const succeeded = document.execCommand('copy')
      selection?.removeAllRanges()
      if (succeeded) {
        showCopied()
      }
    }

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(showCopied).catch(copyUsingSelection)
    } else {
      copyUsingSelection()
    }
  }

  openDeleteModal (event: IdParamEvent): void {
    this.pendingId = event.params.id
    this.deleteModal.show()
  }

  async confirmDelete (): Promise<void> {
    try {
      await fetchWithHandlingLoginRedirect(`${this.createUrlValue}/${this.pendingId}`, {
        method: 'DELETE',
        headers: { Accept: 'application/json' }
      })
      this.dispatch('submitted', { detail: { action: 'deleted', id: this.pendingId } })
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e)
    }
    this.deleteModal.hide()
  }

  openRotateModal (event: IdParamEvent): void {
    this.pendingId = event.params.id
    this.rotateModal.show()
  }

  async confirmRotate (): Promise<void> {
    try {
      const response = await fetchWithHandlingLoginRedirect(`${this.createUrlValue}/${this.pendingId}/rotate-secret`, {
        method: 'POST',
        headers: { Accept: 'application/json' }
      })
      const body = await response.json() as { result: WebhookSubscriptionResult }
      this.rotateModal.hide()
      this.showSecret(body.result.secret as string)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e)
    }
  }

  async enable (event: IdParamEvent): Promise<void> {
    await this.setActiveState(`${this.createUrlValue}/${event.params.id}/enable`)
  }

  async disable (event: IdParamEvent): Promise<void> {
    await this.setActiveState(`${this.createUrlValue}/${event.params.id}/disable`)
  }

  private async setActiveState (url: string): Promise<void> {
    try {
      const response = await fetchWithHandlingLoginRedirect(url, {
        method: 'PATCH',
        headers: { Accept: 'application/json' }
      })
      const body = await response.json() as { result: WebhookSubscriptionResult }
      this.dispatch('submitted', { detail: { action: 'statusChanged', subscription: body.result } })
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e)
    }
  }
}
